'use client';

import { useEffect, useRef, useState } from 'react';

const HOLD_MS = 5 * 60 * 1000;

export const MILES_HOLD_EXPIRED_TEXT = 'Milleriniz birazdan iade edilecek. 15 dk içinde tekrar deneyin.';

type MilesStamp = {
  issuedAt?: string;
  reversedAt?: string | null;
  quantity?: number;
};

export function openMilesIssuedAt(miles?: MilesStamp | null) {
  if (!miles?.issuedAt || miles.reversedAt || !(Number(miles.quantity) > 0)) return undefined;
  return miles.issuedAt;
}

export function cardRemainderAmount(payment?: {
  miles?: MilesStamp | null;
  card?: { amount?: number | null };
} | null) {
  if (!openMilesIssuedAt(payment?.miles)) return null;
  const amount = Number(payment?.card?.amount);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

export function milesHoldRemainingMs(issuedAt?: string, now = Date.now()) {
  if (!issuedAt) return null;
  const issued = new Date(issuedAt).getTime();
  if (!Number.isFinite(issued)) return null;
  return Math.max(0, issued + HOLD_MS - now);
}

export function useMilesHold(issuedAt?: string) {
  const [remaining, setRemaining] = useState<number | null>(() =>
    issuedAt ? milesHoldRemainingMs(issuedAt) : null,
  );
  const issuedRef = useRef(issuedAt);
  if (issuedRef.current !== issuedAt) {
    issuedRef.current = issuedAt;
    setRemaining(issuedAt ? milesHoldRemainingMs(issuedAt) : null);
  }

  useEffect(() => {
    if (!issuedAt) return undefined;
    const tick = () => setRemaining(milesHoldRemainingMs(issuedAt));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [issuedAt]);

  return {
    remaining,
    closed: Boolean(issuedAt) && remaining === 0,
  };
}

export function MilesHoldCountdown({ remaining }: { remaining: number | null }) {
  if (remaining == null) return null;
  if (remaining <= 0) {
    return (
      <p className="rounded-xl bg-[#F6F5F1] px-3.5 py-3 text-sm text-[#9A3412]">
        {MILES_HOLD_EXPIRED_TEXT}
      </p>
    );
  }
  const totalSeconds = Math.ceil(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return (
    <p className="flex items-center justify-between gap-3 rounded-xl bg-[#F6F5F1] px-3.5 py-3 text-sm text-[#212121]">
      <span>Mil tutma süresi</span>
      <span className="font-semibold tabular-nums text-[#2B3280]">
        {minutes}:{String(seconds).padStart(2, '0')}
      </span>
    </p>
  );
}
